/**
 * A module sold on top of the package (certifications, qualifications): tried for free once, bought
 * per month along with the running subscription period, stopped at the end of it.
 *
 * Whether the module may be *used* is not read from here: that is `features.<key>.hasAccess` on the
 * organisation, which is true for a package that includes it, a bought add-on and a running trial
 * alike. This is only what the subscription page offers and says about it.
 */
export interface SubscriptionAddOn {
  id: string;
  /** Not a label: names and descriptions are translated per key in the frontend. */
  key: string;
  featureKeys: string[];
  /** Per month, for the organisation's own tier; null above it (on request). */
  monthlyPrice: number | null;
  /** Bought, and part of the running period. */
  active: boolean;
  /** Set once stopped: still active until then. Null renews along with the subscription. */
  endsAt: string | null;
  trialDays: number | null;
  /** End of the trial, running or past; null while the trial was never used. */
  trialEndDate: string | null;
}

/** What `activate` would bill (dry run) or billed. */
export interface SubscriptionAddOnQuote {
  dryRun: boolean;
  /** Billed now: the monthly price times the months left in the period, a begun month counting whole. 0 during a subscription trial. */
  price: number;
  monthlyPrice: number;
  /** From when it is billed: now, or the end of a running subscription trial. */
  startDate: string;
  /** End of the running period; after it the add-on is on the regular invoice. */
  endDate: string;
}

/**
 * The state an add-on is shown in, read from the overview top to bottom: the first that holds wins.
 *
 * - `ending`      active, but stopped: active until `endsAt`, nothing to do
 * - `active`      active and renewing: can be stopped
 * - `on-request`  no price for this tier: contact
 * - `trial`       the trial is running: can be bought
 * - `trial-ended` the trial is over: can be bought
 * - `available`   never tried: starts with the free trial (7 days); bought from the trial on
 */
export type SubscriptionAddOnState = 'ending' | 'active' | 'on-request' | 'trial' | 'trial-ended' | 'available';

export const subscriptionAddOnState = (addOn: SubscriptionAddOn, now: Date = new Date()): SubscriptionAddOnState => {
  if (addOn.active && addOn.endsAt) return 'ending';
  if (addOn.active) return 'active';
  if (addOn.monthlyPrice === null) return 'on-request';
  if (addOn.trialEndDate && new Date(addOn.trialEndDate) > now) return 'trial';
  if (addOn.trialEndDate) return 'trial-ended';
  return 'available';
};

export const adaptSubscriptionAddOn = (item: any): SubscriptionAddOn => ({
  id: item.id,
  key: item.key,
  featureKeys: item.featureKeys ?? [],
  monthlyPrice: item.monthlyPrice === null || item.monthlyPrice === undefined ? null : Number(item.monthlyPrice),
  active: !!item.active,
  endsAt: item.endsAt ?? null,
  trialDays: item.trialDays ?? null,
  trialEndDate: item.trialEndDate ?? null,
});
